import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Flex } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { CypressIds } from '../../../../../../../../constants/cypressIds';
import { TabNames } from '../../../../../../../../constants/tabMenuTypes';
import { hideTray, showTray } from '../../../../../../../../redux/modules/tray';
import colors from '../../../../../../../../styles/theme/foundation/colors';
import RequirementTemplateBlueprintType from '../../../../../../../../types/RequirementTemplateBlueprintType';
import { get } from '../../../../../../../../utils/arrayUtil';
import LoadingSpinner from '../../../../../../../Spinner';
import CreateCustomDocumentTypeTray
  from '../../../../../RequirementTemplateRightBox/components/RequirementTemplateBuilderBodyRight/components/TemplateBuilderDocumentPanel/components/CreateCustomDocumentTypeTray';
import TemplateBlockFooter from '../TemplateBlockFooter';

const InsuranceDocumentTemplateBlock = ({ templateBlock, blueprint }) => {
  const dispatch = useDispatch();

  const documentTypeId = useMemo(() => get(templateBlock, 'requirementTemplateBlockProperties.0.requirementId', null), templateBlock);

  const onRequiredToggle = isChecked => {
    templateBlock.required = isChecked;
    dispatch(blueprint.updateRequirementTemplateBlock(templateBlock.id, templateBlock));
  };

  const onBlockDelete = () => {
    dispatch(blueprint.deleteRequirementTemplateBlock(templateBlock.id));
  };

  const showDocumentTray = async () => {
    const documentId = get(templateBlock, 'requirementTemplateBlockProperties.0.requirement.id', null);
    dispatch(blueprint.setCurrentRequirementTemplateBlock(templateBlock.id));
    dispatch(blueprint.setCreateCustomStep(2, TabNames.Documents));
    dispatch(blueprint.setEditCustomDocumentId(documentId));
    await dispatch(blueprint.listDeletedDocumentTypes());

    dispatch(showTray(
      () => <CreateCustomDocumentTypeTray blueprint={blueprint} />,
      {
        onClose: () => {
          dispatch(hideTray());
          dispatch(blueprint.setEditCustomDocumentId(null));
          dispatch(blueprint.setCreateCustomStep(1, TabNames.Documents));
          dispatch(blueprint.resetCurrentRequirementTemplateBlock());
          dispatch(blueprint.resetCreateCustomDocumentType());
        }
      }
    ));
  };

  if (templateBlock) {
    return (
      <Box
        _hover={{ boxShadow: 'hover2' }}
        bgColor={colors.primaryScale['50']}
        borderRadius="10px"
        boxShadow="sharp4"
        data-cy={CypressIds.docTypeBlock(documentTypeId)}
        flexBasis="100%"
        layerStyle="panel"
        maxH="35vh"
        mb={4}
        minH="100px"
        minW="480px"
        mr={4}
        mt={1}
        textAlign="left"
        w="100%"
      >
        { templateBlock?.requirementTemplateBlockProperties.length > 0 && (
          <Flex
            borderBottom={`{1px solid ${colors.greyScale['200']}`}
            direction="column"
            justifyContent="flex-start"
            mb={2}
            mt={2}
            w="100%"
          >
            <Box pt={6} px={6} onClick={showDocumentTray}>
              <Box color={colors.greyScale['900']} overflowWrap="anywhere" textStyle="bodyMd600">
                {
                get(templateBlock, 'requirementTemplateBlockProperties.0.requirement.displayName', '')
              }
              </Box>
              <Box color={colors.greyScale['700']} mt={3} overflowWrap="anywhere" textAlign="left" textStyle="bodySm">
                {
                get(templateBlock, 'requirementTemplateBlockProperties.0.requirement.description', '')
              }
              </Box>
            </Box>
            <TemplateBlockFooter
              blueprint={blueprint}
              borderColor={colors.primaryScale['100']}
              cypressId={CypressIds.docTypeDeleteButton(documentTypeId)}
              dusk={`insurance_document_type-${documentTypeId}-delete`}
              isChecked={templateBlock?.required && templateBlock.required > 0}
              requirementType="document_type"
              requirementTypeId={documentTypeId}
              templateBlock={templateBlock}
              onChange={onRequiredToggle}
              onDelete={onBlockDelete}
            />
          </Flex>
        )}
        { !templateBlock?.requirementTemplateBlockProperties?.length > 0 && (
          <Box mt={8}>
            <LoadingSpinner isInline />
          </Box>
        )}
      </Box>
    );
  }

  return null;
};

InsuranceDocumentTemplateBlock.propTypes = {
  blueprint: RequirementTemplateBlueprintType,
  templateBlock: PropTypes.shape({
    required: PropTypes.bool,
    requirementTemplateBlockProperties: PropTypes.arrayOf(PropTypes.shape({}))
  })
};

export default InsuranceDocumentTemplateBlock;
